import moment from 'moment';
import { toLower } from 'Utils/lodashUtils';

export function isValidDate(date, format) {
  // do sanity checks first
  if (moment.isMoment(date)) {
    // already a moment object
    return true;
  } else if (moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD') === date) {
    // valid ISO date format
    return true;
  } else if (!date || !date.split) {
    // if not at least a string, we can't do any more tests, so definitely not a date
    return false;
  }

  // splitting date string based on '/' like 03/12/2018 into an array ['03','12','2018'] then validating
  const split = date.split('/');
  if (split.length !== 3) return false;
  if (split[2].length < 4) return false;
  if (split[0] === '0' || split[0] === '00' || split[0].length > 2) {
    return false;
  }
  if (split[1] === '0' || split[1] === '00' || split[1].length > 2) {
    return false;
  }
  return moment(date, format).format(format).toString() !== 'Invalid date';
}

/**
 * this maps the relative date range translations to their API constants, for use in the preset buttons
 * @param  {object} translations  the translation object passed in through props
 * @return {array}                list of text/value objects
 */
export function getAvailableRanges(translations) {
  let isProductReportView = localStorage.getItem('isProductReportView');
  if (isProductReportView === null ) {
    return [
      {
        text: translations.yesterday,
        value: 'yesterday',
      },
      {
        text: translations.lastSevenDays,
        value: 'last_seven_days',
      },
      {
        text: translations.lastMonth,
        value: 'last_month',
      },
      {
        text: translations.thisMonth,
        value: 'this_month',
      },
      {
        text: translations.thisYear,
        value: 'this_year',
      },
      {
        text: translations.custom,
        value: 'custom',
      },
    ];
  } else {
    return [
      {
        text: translations.yesterday,
        value: 'yesterday',
      },
      {
        text: translations.lastSevenDays,
        value: 'last_seven_days',
      },
      {
        text: translations.lastMonth,
        value: 'last_month',
      },
      {
        text: translations.thisMonth,
        value: 'this_month',
      },
      {
        text: translations.custom,
        value: 'custom',
      },
    ];
  }
}

/**
 * this maps the schedule frequency translations to their API constants, for use in the scheduler
 * @param  {object} translations  the translation object passed in through props
 * @return {array}                list of text/value objects
 */
export function getAvailableScheduleFrequencies(translations) {
  return [
    {
      text: translations.daily,
      value: 'DAILY',
    },
    {
      text: translations.weekly,
      value: 'WEEKLY',
    },
  ];
}

/**
 * this maps the schedule frequency translations to their API constants, for use in the scheduler
 * @param  {object} translations  the translation object passed in through props
 * @return {array}                list of text/value objects
 */
export function getDaysOfWeek(translations) {
  return [
    {
      text: translations.sunday,
      value: 'SUNDAY',
    },
    {
      text: translations.monday,
      value: 'MONDAY',
    },
    {
      text: translations.tuesday,
      value: 'TUESDAY',
    },
    {
      text: translations.wednesday,
      value: 'WEDNESDAY',
    },
    {
      text: translations.thursday,
      value: 'THURSDAY',
    },
    {
      text: translations.friday,
      value: 'FRIDAY',
    },
    {
      text: translations.saturday,
      value: 'SATURDAY',
    },
  ];
}

/**
 * used to display a countdown of time remaining for a process
 * @param  {number} stopTime    Unix timestamp of when process will stop
 * @param  {number} currentTime Unix timestamp to use for difference
 * @return {string}             minutes and seconds remaining, in format '09:09'
 */
export function getTimeRemainingString(stopTime, currentTime) {
  if (currentTime > stopTime) {
    return '00:00';
  } else {
    const totalSecondsRemaining = stopTime - currentTime;
    const minutesRemaining = Math.floor(totalSecondsRemaining / 60);
    const secondsRemaining = totalSecondsRemaining % 60;

    const formattedMinutes = `0${minutesRemaining}`.slice(-2);
    const formattedSeconds = `0${secondsRemaining}`.slice(-2);
    const visualTimer = `${formattedMinutes}:${formattedSeconds}`;
    return visualTimer;
  }
}

/**
 * simple wrapper around a library function to take some kind of "time"
 *   (Date object, ISO date string, Unix timestamp, millisecond timestamp)
 *   and return a unix timestamp of it.
 *   2018-09-12: currently uses moment.js
 * @param  {Date | string | number} stopTime    Date object, ISO date string, Unix timestamp, millisecond timestamp
 * @return {number}             Unix timestamp (number of seconds since the epoch)
 */
export function parseTime(time) {
  return moment(time).unix();
}

export function dateFormatter (date) {
  return moment(new Date(date)).format('MM/DD/YYYY');
}

export const getFormattedDateFormat = (dateFormat) => {
  return dateFormat.split('/').map((val, index) => index > 0 ? toLower(val) : val).join('/') || "";
}

export default {
  isValidDate,
  getAvailableRanges,
  getAvailableScheduleFrequencies,
  getDaysOfWeek,
  getTimeRemainingString,
  parseTime,
  dateFormatter,
  getFormattedDateFormat
};
